if (!customElements.get('cart-discount')) {
  class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[name="discount_code"]');
    this.applyButton = this.querySelector('[data-apply-discount]');
    this.messageContainer = this.querySelector('[data-discount-message]');
    this.appliedContainer = this.querySelector('[data-applied-discounts]');
    this.appliedList = this.querySelector('[data-applied-discounts-list]');
    this.defaultApplyLabel = this.applyButton?.textContent?.trim() || this.dataset.labelApply || 'Apply';

    this.bindEvents();
    this.updateApplyButton();
    this.updateAppliedDiscounts();
  }

  bindEvents() {
    this.input?.addEventListener('input', () => this.updateApplyButton());
    this.input?.addEventListener('keypress', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.applyDiscount();
      }
    });

    this.applyButton?.addEventListener('click', () => this.applyDiscount());
    document.addEventListener('cart:change', () => this.updateAppliedDiscounts());
  }

  updateApplyButton() {
    if (!this.applyButton || !this.input) return;
    this.applyButton.disabled = !this.input.value.trim();
  }

  async applyDiscount() {
    const code = this.input?.value.trim();
    if (!code || !this.applyButton) return;

    this.applyButton.disabled = true;
    this.applyButton.textContent = this.dataset.labelApplying || 'Applying...';
    this.hideMessage();

    try {
      const beforeCart = await (await fetch('/cart.js')).json();
      const beforeDiscounts = beforeCart.cart_level_discount_applications || [];
      const beforeTotal = beforeCart.total_price;
      const existingCodes = (beforeCart.discount_codes || [])
        .filter((discountCode) => discountCode.applicable)
        .map((discountCode) => discountCode.code);

      if (existingCodes.some((existingCode) => existingCode.toLowerCase() === code.toLowerCase())) {
        this.showMessage(this.dataset.labelAlreadyApplied || 'This discount code is already applied', 'error');
        return;
      }

      const response = await this.updateDiscountCodes([...existingCodes, code]);

      if (response.ok) {
        const afterCart = await response.json();
        const afterDiscounts = afterCart.cart_level_discount_applications || [];
        const afterTotal = afterCart.total_price;
        const codeEntry = (afterCart.discount_codes || []).find(
          (discountCode) => discountCode.code.toLowerCase() === code.toLowerCase()
        );
        const wasApplied = codeEntry
          ? codeEntry.applicable
          : afterDiscounts.length > beforeDiscounts.length || afterTotal < beforeTotal;

        if (wasApplied) {
          this.showMessage(this.dataset.labelSuccess || 'Discount code applied successfully!', 'success');
          this.input.value = '';
          this.updateAppliedDiscounts();
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } else {
          await this.updateDiscountCodes(existingCodes);

          const hasOtherDiscounts = existingCodes.length > 0 || beforeCart.total_discount > 0;
          if (hasOtherDiscounts) {
            this.showMessage(
              this.dataset.labelErrorCombination || "This code can't be combined with the other discounts in your cart",
              'error'
            );
          } else {
            this.showMessage(this.dataset.labelError || 'Invalid discount code', 'error');
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        this.showMessage(errorData.description || this.dataset.labelError || 'Invalid discount code', 'error');
      }
    } catch (error) {
      console.error('Discount application error:', error);
      this.showMessage(this.dataset.labelErrorFallback || this.dataset.labelError || 'Error applying discount code', 'error');
    } finally {
      if (this.applyButton) {
        this.applyButton.disabled = false;
        this.applyButton.textContent = this.defaultApplyLabel;
      }
      this.updateApplyButton();
    }
  }

  updateDiscountCodes(codes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ discount: codes.join(',') })
    });
  }

  async removeDiscount() {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ discount: '' })
      });

      if (response.ok) {
        this.updateAppliedDiscounts();
        document.documentElement.dispatchEvent(new CustomEvent('cart:discount-removed', { bubbles: true }));
      }
    } catch (error) {
      console.error('Error removing discount:', error);
    }
  }

  updateAppliedDiscounts() {
    if (!this.appliedContainer || !this.appliedList) return;

    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        const discountApplications = cart.cart_level_discount_applications || [];
        const removeLabel = this.dataset.labelRemove || 'Remove';

        if (discountApplications.length > 0) {
          this.appliedList.innerHTML = '';

          discountApplications.forEach((discount) => {
            const discountElement = document.createElement('div');
            discountElement.className = 'h-stack justify-between gap-2 p-2 bg-subdued rounded';
            discountElement.innerHTML = `
              <span class="subheading">${discount.title}</span>
              <button
                type="button"
                class="button button--sm button--outline"
                data-remove-discount
                aria-label="Remove ${discount.title}"
              >
                ${removeLabel}
              </button>
            `;

            discountElement.querySelector('[data-remove-discount]')?.addEventListener('click', () => {
              this.removeDiscount();
            });

            this.appliedList.appendChild(discountElement);
          });

          this.appliedContainer.hidden = false;
        } else {
          this.appliedContainer.hidden = true;
        }
      })
      .catch((error) => {
        console.error('Error fetching cart:', error);
      });
  }

  showMessage(message, type = 'info') {
    if (!this.messageContainer) return;

    this.messageContainer.textContent = message;
    this.messageContainer.className = `cart-discount__message text-${type}`;
    this.messageContainer.hidden = false;

    if (type === 'success') {
      setTimeout(() => this.hideMessage(), 3000);
    }
  }

  hideMessage() {
    if (this.messageContainer) {
      this.messageContainer.hidden = true;
    }
  }
  }

  customElements.define('cart-discount', CartDiscount);
}
